import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { brand, marketing, radii } from '@lunarcrush/ds/tokens';
import { useColorScheme, type ColorScheme } from './hooks/useColorScheme';

// DEV-10764: light/dark toggle for the public site. Sits next to the
// LanguagePicker in the navbar (compact) and inside the MobileNav drawer
// (full). The chosen scheme is persisted to an apex cookie so onboarding
// and the web app inherit it — see useColorScheme.

const SunIcon: React.FC<{ size?: number; color: string }> = ({ size = 16, color }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill={color} aria-hidden>
    <path d="M120,40V16a8,8,0,0,1,16,0V40a8,8,0,0,1-16,0Zm72,88a64,64,0,1,1-64-64A64.07,64.07,0,0,1,192,128Zm-16,0a48,48,0,1,0-48,48A48.05,48.05,0,0,0,176,128ZM58.34,69.66A8,8,0,0,0,69.66,58.34l-16-16A8,8,0,0,0,42.34,53.66Zm0,116.68-16,16a8,8,0,0,0,11.32,11.32l16-16a8,8,0,0,0-11.32-11.32ZM192,72a8,8,0,0,0,5.66-2.34l16-16a8,8,0,0,0-11.32-11.32l-16,16A8,8,0,0,0,192,72Zm5.66,114.34a8,8,0,0,0-11.32,11.32l16,16a8,8,0,0,0,11.32-11.32ZM48,128a8,8,0,0,0-8-8H16a8,8,0,0,0,0,16H40A8,8,0,0,0,48,128Zm80,80a8,8,0,0,0-8,8v24a8,8,0,0,0,16,0V216A8,8,0,0,0,128,208Zm112-88H216a8,8,0,0,0,0,16h24a8,8,0,0,0,0-16Z" />
  </svg>
);

const MoonIcon: React.FC<{ size?: number; color: string }> = ({ size = 16, color }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill={color} aria-hidden>
    <path d="M233.54,142.23a8,8,0,0,0-8-2,88.08,88.08,0,0,1-109.8-109.8,8,8,0,0,0-10-10,104.84,104.84,0,0,0-52.91,37A104,104,0,0,0,136,224a103.09,103.09,0,0,0,62.52-20.88,104.84,104.84,0,0,0,37-52.91A8,8,0,0,0,233.54,142.23ZM188.9,190.34A88,88,0,0,1,65.66,67.11a89,89,0,0,1,31.4-26A106,106,0,0,0,96,56,104.11,104.11,0,0,0,200,160a106,106,0,0,0,14.92-1.06A89,89,0,0,1,188.9,190.34Z" />
  </svg>
);

interface ThemePickerProps {
  /** `compact` = navbar icon button. `full` = labelled segmented row for the mobile drawer. */
  variant?: 'compact' | 'full';
}

export const ThemePicker: React.FC<ThemePickerProps> = ({ variant = 'compact' }) => {
  const { t } = useTranslation();
  const { scheme, setColorScheme, toggle } = useColorScheme();
  const [hovered, setHovered] = useState(false);

  // Compact: a single icon button that flips the theme. We show the icon of
  // the scheme the user will switch TO (sun while dark, moon while light).
  if (variant === 'compact') {
    const color = hovered ? marketing.textHeading : marketing.textBody;
    const next: ColorScheme = scheme === 'dark' ? 'light' : 'dark';
    const label = t('theme.switchTo', { defaultValue: `Switch to ${next} mode` });
    return (
      <button
        type="button"
        onClick={toggle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        aria-label={label}
        title={label}
        style={{
          background: 'transparent',
          border: 'none',
          color,
          // DEV-11282 re-work: slim horizontal padding — the invisible hit
          // area read as extra spacing between the navbar controls.
          padding: '8px 4px',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'color 200ms ease',
        }}
      >
        {scheme === 'dark'
          ? <SunIcon size={18} color={color} />
          : <MoonIcon size={18} color={color} />}
      </button>
    );
  }

  // Full: labelled segmented control for the mobile drawer.
  const options: { code: ColorScheme; icon: React.FC<{ size?: number; color: string }>; labelKey: string; fallback: string }[] = [
    { code: 'dark', icon: MoonIcon, labelKey: 'theme.dark', fallback: 'Dark' },
    { code: 'light', icon: SunIcon, labelKey: 'theme.light', fallback: 'Light' },
  ];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        gap: 12,
      }}
    >
      <span
        style={{
          color: marketing.textBody,
          fontSize: 15,
          fontFamily: 'Plus Jakarta Sans',
          fontWeight: 500,
        }}
      >
        {t('theme.appearance', { defaultValue: 'Appearance' })}
      </span>
      <div
        role="radiogroup"
        aria-label={t('theme.appearance', { defaultValue: 'Appearance' })}
        style={{
          display: 'inline-flex',
          background: marketing.surfaceDark,
          border: `1px solid ${marketing.border}`,
          borderRadius: radii.md,
          padding: 3,
          gap: 3,
        }}
      >
        {options.map(({ code, icon: Icon, labelKey, fallback }) => {
          const active = scheme === code;
          const color = active ? brand.white : marketing.textBody;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setColorScheme(code)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                borderRadius: radii.sm,
                border: 'none',
                cursor: 'pointer',
                background: active ? brand.purple : 'transparent',
                color,
                fontSize: 14,
                fontFamily: 'Plus Jakarta Sans',
                fontWeight: active ? 600 : 500,
                transition: 'background-color 200ms ease, color 200ms ease',
              }}
            >
              <Icon size={16} color={color} />
              <span>{t(labelKey, { defaultValue: fallback })}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

ThemePicker.displayName = 'ThemePicker';
